import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Users, Calendar, DollarSign, Image, TrendingUp, Activity, 
  Package, BarChart3, Clock,
  CheckCircle, Target, Award, Zap,
  ArrowUp, ArrowDown
} from "lucide-react";

const Dashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setRefreshing(true);
    try {
      // Updated API endpoint
      const response = await axios.get("https://api.dreamtoday.in/api/admin/dashboard", {
        withCredentials: true
      });
      
      console.log("Dashboard response:", response.data);
      
      if (response.data.success) {
        const data = response.data.dashboard;
        
        // Format recent bookings
        const recentActivities = data.recentBookings?.slice(0, 5).map(booking => {
          const userName = booking.userId?.name || "Unknown User";
          const campName = booking.campaignId?.title || "Unknown Campaign";
          const amount = booking.totalAmount || 0;
          
          const createdAt = new Date(booking.bookingDate);
          const now = new Date();
          const diffInMinutes = Math.floor((now - createdAt) / (1000 * 60));
          const diffInHours = Math.floor(diffInMinutes / 60);
          const diffInDays = Math.floor(diffInHours / 24);
          
          let timeAgo = "";
          if (diffInDays > 0) {
            timeAgo = `${diffInDays}d ago`;
          } else if (diffInHours > 0) {
            timeAgo = `${diffInHours}h ago`;
          } else {
            timeAgo = `${diffInMinutes}m ago`;
          }
          
          return {
            id: booking._id,
            user: userName,
            camp: campName,
            amount: amount,
            time: timeAgo,
            avatar: userName.charAt(0).toUpperCase()
          };
        }) || [];
        
        // Prepare dashboard data object with actual API data
        const formattedData = {
          totalUsers: data.totalUsers || 0,
          totalCamps: data.totalCampaigns || 0,
          totalPlans: data.totalPlans || 0,
          totalBanners: data.totalBanners || 0,
          totalRevenue: data.totalRevenue || 0,
          totalCampBookings: data.totalBookings || 0,
          
          // Growth percentages - calculated from actual data
          userGrowth: calculateGrowth(data.totalUsers, 0),
          campsGrowth: calculateGrowth(data.totalCampaigns, 0),
          plansGrowth: calculateGrowth(data.totalPlans, 0),
          bannersGrowth: calculateGrowth(data.totalBanners, 0),
          revenueGrowth: calculateGrowth(data.totalRevenue, 0),
          
          recentActivities: recentActivities,
          
          statsOverview: {
            avgBookingValue: data.avgBooking?.toFixed(2) || "0.00",
            totalTransactions: data.totalBookings || 0,
            activeCamps: data.activeCampaigns || 0,
            activePlans: data.activePlans || 0
          }
        };
        
        setDashboardData(formattedData);
      } else {
        throw new Error("Failed to fetch dashboard data");
      }
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
      
      // Show error message but don't set dummy data
      setDashboardData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Helper function to calculate growth (you can implement actual logic)
  const calculateGrowth = (current, previous) => {
    if (previous === 0) return 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  const refreshData = () => {
    fetchDashboardData();
  };

  // Navigation handlers
  const handleUsersClick = () => {
    navigate("/users");
  };

  const handleCampaignsClick = () => {
    navigate("/camp");
  };

  const handlePlansClick = () => {
    navigate("/planlist");
  };

  const handleBannersClick = () => {
    navigate("/bannerlist");
  };

  const handleRevenueClick = () => {
    navigate("/purchasedplans");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl font-bold text-white">L</span>
            </div>
          </div>
          <p className="mt-4 text-gray-300">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center">
        <div className="text-center bg-white/10 backdrop-blur-lg rounded-2xl p-8">
          <Activity className="h-12 w-12 text-red-400 mx-auto" />
          <p className="mt-4 text-gray-200">Failed to load dashboard data</p>
          <p className="text-sm text-gray-400 mt-2">Please check your connection</p>
          <button 
            onClick={refreshData}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const {
    totalUsers,
    totalCamps,
    totalPlans,
    totalBanners,
    totalRevenue,
    totalCampBookings,
    userGrowth,
    campsGrowth,
    plansGrowth,
    bannersGrowth,
    revenueGrowth,
    recentActivities,
    statsOverview
  } = dashboardData;

  // Main stat cards with navigation
  const statCards = [
    { 
      label: "Total Users", 
      value: totalUsers.toLocaleString(), 
      icon: Users,
      gradient: "from-blue-600 to-blue-400",
      growth: userGrowth,
      trend: userGrowth > 0 ? "up" : "down",
      change: userGrowth > 0 ? "+" + userGrowth + "%" : userGrowth + "%",
      onClick: handleUsersClick
    },
    { 
      label: "Total Campaigns", 
      value: totalCamps.toLocaleString(), 
      icon: Target,
      gradient: "from-purple-600 to-purple-400",
      growth: campsGrowth,
      trend: campsGrowth > 0 ? "up" : "down",
      change: campsGrowth > 0 ? "+" + campsGrowth + "%" : campsGrowth + "%",
      onClick: handleCampaignsClick
    },
    { 
      label: "Total Plans", 
      value: totalPlans.toLocaleString(), 
      icon: Award,
      gradient: "from-emerald-600 to-emerald-400",
      growth: plansGrowth,
      trend: plansGrowth > 0 ? "up" : "down",
      change: plansGrowth > 0 ? "+" + plansGrowth + "%" : plansGrowth + "%",
      onClick: handlePlansClick
    },
    { 
      label: "Total Banners", 
      value: totalBanners.toLocaleString(), 
      icon: Image,
      gradient: "from-amber-600 to-amber-400",
      growth: bannersGrowth,
      trend: bannersGrowth > 0 ? "up" : "down",
      change: bannersGrowth > 0 ? "+" + bannersGrowth + "%" : bannersGrowth + "%",
      onClick: handleBannersClick
    },
    { 
      label: "Total Revenue", 
      value: `₹${totalRevenue.toLocaleString()}`, 
      icon: DollarSign,
      gradient: "from-green-600 to-green-400",
      growth: revenueGrowth,
      trend: revenueGrowth > 0 ? "up" : "down",
      change: revenueGrowth > 0 ? "+" + revenueGrowth + "%" : revenueGrowth + "%",
      onClick: handleRevenueClick
    }
  ];

  // Quick stats - from actual data
  const quickStats = [
    { 
      label: "Camp Bookings", 
      value: totalCampBookings.toLocaleString(),
      icon: Calendar,
      bgColor: "bg-indigo-100",
      iconColor: "text-indigo-600"
    },
    { 
      label: "Avg Booking", 
      value: `₹${statsOverview.avgBookingValue}`,
      icon: BarChart3,
      bgColor: "bg-pink-100",
      iconColor: "text-pink-600"
    },
    { 
      label: "Active Camps", 
      value: statsOverview.activeCamps.toLocaleString(),
      icon: Zap,
      bgColor: "bg-orange-100",
      iconColor: "text-orange-600"
    },
    { 
      label: "Active Plans", 
      value: statsOverview.activePlans.toLocaleString(),
      icon: Package,
      bgColor: "bg-cyan-100",
      iconColor: "text-cyan-600"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Removed search icon */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white">
        <div className="px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 backdrop-blur-lg rounded-xl p-2">
                <span className="text-2xl font-bold">DreamToday</span>
              </div>
              <div className="h-8 w-px bg-white/20"></div>
              <div>
                <h1 className="text-xl font-semibold">Admin Dashboard</h1>
                <p className="text-sm text-white/70">Welcome back, Admin</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 bg-white/20 rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold">A</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {/* Welcome Card */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 mb-8 text-white">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold mb-2">Welcome to DreamToday Dashboard</h2>
              <p className="text-white/80">Manage your campaigns, plans, and monitor performance</p>
            </div>
            <button
              onClick={refreshData}
              disabled={refreshing}
              className="px-4 py-2 bg-white/20 backdrop-blur-lg rounded-lg hover:bg-white/30 transition flex items-center gap-2 disabled:opacity-50"
            >
              <Activity className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh Data'}
            </button>
          </div>
        </div>

        {/* Stats Cards - With navigation */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            
            return (
              <div 
                key={index}
                onClick={stat.onClick}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 rounded-xl bg-gradient-to-r ${stat.gradient} bg-opacity-10`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    {stat.growth !== 0 && (
                      <div className="flex items-center gap-1 text-sm">
                        {stat.trend === 'up' ? (
                          <ArrowUp className="h-4 w-4 text-green-500" />
                        ) : (
                          <ArrowDown className="h-4 w-4 text-red-500" />
                        )}
                        <span className={stat.trend === 'up' ? 'text-green-600' : 'text-red-600'}>
                          {stat.change}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</h3>
                  <p className="text-sm text-gray-600">{stat.label}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Quick Stats - HORIZONTAL ROW */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {quickStats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                    <Icon className={`h-6 w-6 ${stat.iconColor}`} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{stat.label}</p>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Recent Activities and Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Recent Activities */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Recent Bookings</h2>
                <span className="text-sm text-gray-500">Latest {recentActivities.length} bookings</span>
              </div>
              
              <div className="space-y-4">
                {recentActivities.length > 0 ? (
                  recentActivities.map((activity, index) => (
                    <div key={index} className="flex items-center p-4 bg-gray-50 rounded-xl hover:bg-blue-50 transition-all group">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold mr-4">
                        {activity.avatar}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-gray-900 group-hover:text-blue-600 transition">
                            {activity.user}
                          </p>
                          <span className="text-sm text-gray-500">{activity.time}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500 mt-1">
                          <span>Booked: {activity.camp}</span>
                          <span className="mx-2">•</span>
                          <span className="text-green-600 font-medium">₹{activity.amount.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No recent bookings found</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Summary Card */}
          <div>
            <div className="bg-white rounded-2xl shadow-lg p-6 h-full">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Summary</h2>
              
              <div className="p-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl text-white mb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-white/80">Total Transactions</p>
                  <Calendar className="h-5 w-5 text-white/80" />
                </div>
                <p className="text-3xl font-bold mb-1">{statsOverview.totalTransactions}</p>
                <p className="text-xs text-white/60">All time</p>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Active Campaigns</span>
                  <span className="font-semibold text-gray-900">{statsOverview.activeCamps}/{totalCamps}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Active Plans</span>
                  <span className="font-semibold text-gray-900">{statsOverview.activePlans}/{totalPlans}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Avg Booking</span>
                  <span className="font-semibold text-green-600">₹{statsOverview.avgBookingValue}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Active Campaigns Rate */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-100 rounded-xl">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <span className="text-2xl font-bold text-gray-900">
                {totalCamps > 0 ? Math.round((statsOverview.activeCamps / totalCamps) * 100) : 0}%
              </span>
            </div>
            <h3 className="text-gray-900 font-medium mb-1">Active Campaigns Rate</h3>
            <p className="text-sm text-gray-500">{statsOverview.activeCamps} out of {totalCamps} campaigns active</p>
            <div className="mt-4 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full"
                style={{ width: `${totalCamps > 0 ? (statsOverview.activeCamps / totalCamps) * 100 : 0}%` }}
              ></div>
            </div>
          </div>

          {/* Plans Utilization */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-100 rounded-xl">
                <Award className="h-6 w-6 text-purple-600" />
              </div>
              <span className="text-2xl font-bold text-gray-900">
                {totalPlans > 0 ? Math.round((statsOverview.activePlans / totalPlans) * 100) : 0}%
              </span>
            </div>
            <h3 className="text-gray-900 font-medium mb-1">Plans Utilization</h3>
            <p className="text-sm text-gray-500">{statsOverview.activePlans} out of {totalPlans} plans in use</p>
            <div className="mt-4 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full"
                style={{ width: `${totalPlans > 0 ? (statsOverview.activePlans / totalPlans) * 100 : 0}%` }}
              ></div>
            </div>
          </div>

          {/* Revenue per Booking */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-amber-100 rounded-xl">
                <DollarSign className="h-6 w-6 text-amber-600" />
              </div>
              <span className="text-2xl font-bold text-gray-900">₹{statsOverview.avgBookingValue}</span>
            </div>
            <h3 className="text-gray-900 font-medium mb-1">Revenue per Booking</h3>
            <p className="text-sm text-gray-500">Average amount per transaction</p>
            <div className="mt-4 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-500" />
              <span className="text-sm text-green-600">Based on {totalCampBookings} bookings</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>© 2026 DreamToday Admin Panel. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;